(function () {
  const VIEWED_KEY = 'bn.forYou.recentlyViewed.v1';
  const FAV_KEY = 'bn.forYou.favourites.v1';
  const DASHBOARD_PREFS_PREFIX = 'bn.forYou.dashboardPrefs.v1';
  const MAX_STORED = 24;
  const HISTORY_ROUTES = {
    'recently-viewed': '/pages/for-you-recently-viewed',
    'buy-again': '/pages/for-you-buy-it-again',
  };
  let purchasedProductsCache = null;
  const candidateProductsCache = new Map();
  const liveProductCache = new Map();
  const STOP_WORDS = new Set([
    'and', 'the', 'with', 'from', 'your', 'that', 'this', 'have', 'will',
    'into', 'more', 'just', 'about', 'shop', 'product', 'products', 'organic',
    'natural', 'eco', 'bn', 'australia', 'support', 'formula', 'capsules',
    'powder', 'tablets', 'spray', 'cream', 'liquid', 'food', 'wellness'
  ]);
  const SIGNAL_WEIGHTS = {
    purchased: 7,
    favourite: 4,
    viewed: 2,
  };
  const THEME_CLUSTERS = [
    {
      key: 'gut-health',
      label: 'gut health',
      keywords: ['gut', 'digest', 'digestion', 'fibre', 'fiber', 'probiotic', 'prebiotic', 'fodmap', 'microbiome', 'colon', 'bowel'],
    },
    {
      key: 'sleep-recovery',
      label: 'sleep and recovery',
      keywords: ['sleep', 'recovery', 'magnesium', 'calm', 'relax', 'stress', 'night', 'melatonin', 'rest'],
    },
    {
      key: 'low-tox-home',
      label: 'low-tox home',
      keywords: ['cleaner', 'cleaning', 'laundry', 'dish', 'surface', 'detergent', 'home', 'household', 'toxin', 'toxic', 'deodorant'],
    },
    {
      key: 'pantry-essentials',
      label: 'pantry essentials',
      keywords: ['pantry', 'tea', 'coffee', 'oats', 'broth', 'snack', 'protein', 'kitchen', 'baking', 'seed', 'oil'],
    },
    {
      key: 'skin-beauty',
      label: 'skin and beauty',
      keywords: ['skin', 'beauty', 'collagen', 'face', 'serum', 'hair', 'glow', 'moistur', 'sunscreen'],
    },
    {
      key: 'immune-wellness',
      label: 'immunity and wellness',
      keywords: ['immune', 'immunity', 'vitamin c', 'zinc', 'elderberry', 'wellness'],
    },
    {
      key: 'womens-health',
      label: 'women’s health',
      keywords: ['women', 'womens', 'menopause', 'period', 'hormone', 'fertility', 'prenatal'],
    },
  ];

  function normalizeToken(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function readJson(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      if (Array.isArray(fallback)) {
        return Array.isArray(parsed) ? parsed : fallback;
      }
      if (fallback && typeof fallback === 'object') {
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
      }
      return parsed ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      return false;
    }
    return true;
  }

  function getCardData(el) {
    if (!el) return null;
    const handle = (el.dataset.productHandle || '').trim();
    if (!handle) return null;

    return {
      handle,
      title: el.dataset.productTitle || '',
      url: el.dataset.productUrl || '',
      vendor: el.dataset.productVendor || '',
      type: el.dataset.productType || '',
      tags: (el.dataset.productTags || '').split('|').filter(Boolean),
      image: el.dataset.productImage || '',
      price: Number(el.dataset.productPrice || 0),
      compareAtPrice: Number(el.dataset.productCompareAt || 0),
      variantId: Number(el.dataset.productVariantId || 0),
      available: el.dataset.productAvailable === 'true',
      createdAt: Number(el.dataset.productCreatedAt || 0),
    };
  }

  function money(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function savings(product) {
    if (!product.compareAtPrice || product.compareAtPrice <= product.price) return 0;
    return Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100);
  }

  function readList(key) {
    return readJson(key, []).filter((item) => item && item.handle);
  }

  function readPurchasedProducts() {
    if (purchasedProductsCache) return purchasedProductsCache;

    const payload = document.querySelector('[data-for-you-purchased-products]');
    if (!payload) {
      purchasedProductsCache = [];
      return purchasedProductsCache;
    }

    try {
      const parsed = JSON.parse(payload.textContent || '[]');
      purchasedProductsCache = Array.isArray(parsed)
        ? parsed.filter((item) => item && item.handle && item.variantId)
        : [];
    } catch (error) {
      purchasedProductsCache = [];
    }

    return purchasedProductsCache;
  }

  function readCandidateProducts(key) {
    if (candidateProductsCache.has(key)) {
      return candidateProductsCache.get(key);
    }

    const payload = document.querySelector(`[data-for-you-candidate-products="${key}"]`);
    if (!payload) {
      candidateProductsCache.set(key, []);
      return [];
    }

    try {
      const parsed = JSON.parse(payload.textContent || '[]');
      const products = Array.isArray(parsed)
        ? parsed.filter((item) => item && item.handle && item.variantId)
        : [];
      candidateProductsCache.set(key, products);
      return products;
    } catch (error) {
      candidateProductsCache.set(key, []);
      return [];
    }
  }

  function writeList(key, items) {
    writeJson(key, items.slice(0, MAX_STORED));
  }

  function dashboardPrefsKey(scope) {
    return `${DASHBOARD_PREFS_PREFIX}.${scope || 'guest'}`;
  }

  function uniqueIds(items) {
    return Array.from(new Set((items || []).filter((item) => typeof item === 'string' && item)));
  }

  function readDashboardPrefs(scope) {
    const key = dashboardPrefsKey(scope);
    let parsed = {};
    let hasStoredPrefs = false;

    try {
      const raw = window.localStorage.getItem(key);
      hasStoredPrefs = Boolean(raw);
      parsed = raw ? JSON.parse(raw) : {};
    } catch (error) {
      parsed = {};
    }

    return {
      order: uniqueIds(parsed.order),
      collapsed: uniqueIds(parsed.collapsed || parsed.hidden),
      hasStoredPrefs,
    };
  }

  function writeDashboardPrefs(scope, prefs) {
    return writeJson(dashboardPrefsKey(scope), {
      order: uniqueIds(prefs.order),
      collapsed: uniqueIds(prefs.collapsed),
    });
  }

  function clearDashboardPrefs(scope) {
    try {
      window.localStorage.removeItem(dashboardPrefsKey(scope));
    } catch (error) {
      return false;
    }
    return true;
  }

  function dedupeByHandle(items) {
    const seen = new Set();
    const result = [];
    for (const item of items) {
      if (!item || !item.handle || seen.has(item.handle)) continue;
      seen.add(item.handle);
      result.push(item);
    }
    return result;
  }

  function updateRecentlyViewed(product) {
    const current = readList(VIEWED_KEY).filter((item) => item.handle !== product.handle);
    current.unshift({
      ...product,
      seenAt: Date.now(),
    });
    writeList(VIEWED_KEY, dedupeByHandle(current));
  }

  function updateFavourites(product, add) {
    const current = readList(FAV_KEY).filter((item) => item.handle !== product.handle);
    if (add) {
      current.unshift({
        ...product,
        savedAt: Date.now(),
      });
    }
    writeList(FAV_KEY, dedupeByHandle(current));
  }

  function isFavourite(handle) {
    return readList(FAV_KEY).some((item) => item.handle === handle);
  }

  async function toggleFavourite(button, product) {
    if (!product) return;
    const favourite = !isFavourite(product.handle);
    updateFavourites(product, favourite);
    await renderModules();
    bindFavouriteButtons();
    bindCardClicks();
    syncFavouriteButtons();
  }

  function cardMarkup(product, options = {}) {
    const save = options.showSave !== false && savings(product) > 0
      ? `<span class="pro-for-you-mini-card__badge">${savings(product)}% OFF RRP</span>`
      : '';
    const compare = product.compareAtPrice > product.price
      ? `<span class="pro-for-you-mini-card__compare">${money(product.compareAtPrice)}</span>`
      : '';

    return `
      <article class="pro-for-you-mini-card" data-for-you-product data-product-handle="${escapeAttr(product.handle)}" data-product-title="${escapeAttr(product.title)}" data-product-url="${escapeAttr(product.url)}" data-product-image="${escapeAttr(product.image)}" data-product-vendor="${escapeAttr(product.vendor)}" data-product-type="${escapeAttr(product.type || '')}" data-product-tags="${escapeAttr(Array.isArray(product.tags) ? product.tags.join('|') : '')}" data-product-price="${product.price}" data-product-compare-at="${product.compareAtPrice}" data-product-variant-id="${product.variantId}" data-product-available="${product.available}" data-product-created-at="${product.createdAt || 0}">
        <div class="pro-for-you-mini-card__media">
          <a class="pro-for-you-mini-card__media-link" href="${escapeAttr(product.url)}" aria-label="${escapeAttr(product.title)}">
            ${product.image ? `<img class="pro-for-you-mini-card__image" src="${escapeAttr(product.image)}" alt="${escapeAttr(product.title)}" loading="lazy">` : '<div class="pro-for-you-mini-card__placeholder" aria-hidden="true"></div>'}
          </a>
          ${save}
        </div>
        <div class="pro-for-you-mini-card__body">
          <p class="pro-for-you-mini-card__vendor">${escapeHtml(product.vendor || '')}</p>
          <a class="pro-for-you-mini-card__title" href="${escapeAttr(product.url)}">${escapeHtml(product.title || '')}</a>
          <div class="pro-for-you-mini-card__price-row">${compare}${product.price ? `<span class="pro-for-you-mini-card__price">${money(product.price)}</span>` : ''}</div>
          <form method="post" action="/cart/add" class="pro-for-you-mini-card__form">
            <input type="hidden" name="id" value="${product.variantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="pro-for-you-mini-card__add" ${product.available ? '' : 'disabled'}>${product.available ? 'Add to Cart' : 'Back soon'}</button>
          </form>
        </div>
      </article>
    `;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  function parseFallbackProducts(moduleEl) {
    const raw = moduleEl?.dataset?.forYouFallbackProducts || '[]';
    try {
      return JSON.parse(raw).filter((item) => item && item.handle);
    } catch (error) {
      return [];
    }
  }

  function getStoredProducts(handles, fallbackProducts) {
    const allKnown = [
      ...readList(VIEWED_KEY),
      ...readList(FAV_KEY),
      ...fallbackProducts,
    ];

    const byHandle = new Map();
    allKnown.forEach((item) => {
      if (item && item.handle) byHandle.set(item.handle, item);
    });

    return handles
      .map((handle) => byHandle.get(handle))
      .filter(Boolean);
  }

  function currentShopperProducts() {
    return dedupeByHandle([
      ...readPurchasedProducts(),
      ...readList(VIEWED_KEY),
      ...readList(FAV_KEY),
    ]);
  }

  function currentShopperHandles() {
    return new Set(currentShopperProducts().map((product) => product.handle));
  }

  function timestampBonus(timestamp, ranges) {
    if (!timestamp) return 0;
    const ageDays = (Date.now() - Number(timestamp)) / 86400000;
    return ranges.reduce((bonus, [days, value]) => {
      return ageDays <= days ? bonus + value : bonus;
    }, 0);
  }

  function buildSignalEntries() {
    const entries = [];

    readPurchasedProducts().forEach((product) => {
      entries.push({
        product,
        source: 'purchased',
        weight: SIGNAL_WEIGHTS.purchased + timestampBonus(product.orderedAt, [[30, 2], [90, 1]]),
      });
    });

    readList(FAV_KEY).forEach((product) => {
      entries.push({
        product,
        source: 'favourite',
        weight: SIGNAL_WEIGHTS.favourite + timestampBonus(product.savedAt, [[14, 2], [45, 1]]),
      });
    });

    readList(VIEWED_KEY).forEach((product) => {
      entries.push({
        product,
        source: 'viewed',
        weight: SIGNAL_WEIGHTS.viewed + timestampBonus(product.seenAt, [[3, 2], [14, 1]]),
      });
    });

    return entries.filter((entry) => entry.product && entry.product.handle);
  }

  function productSignalText(product) {
    return normalizeToken([
      product?.title,
      product?.vendor,
      product?.type,
      Array.isArray(product?.tags) ? product.tags.join(' ') : '',
    ].join(' '));
  }

  function matchThemeClusters(signalText) {
    return THEME_CLUSTERS.map((cluster) => {
      const matches = cluster.keywords
        .map(normalizeToken)
        .filter((keyword) => keyword && signalText.includes(keyword));

      return {
        ...cluster,
        matches: Array.from(new Set(matches)),
      };
    }).filter((cluster) => cluster.matches.length > 0);
  }

  function formatNaturalList(items) {
    const values = items.filter(Boolean);
    if (values.length === 0) return '';
    if (values.length === 1) return values[0];
    if (values.length === 2) return `${values[0]} and ${values[1]}`;
    return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
  }

  function shopperMaturity(counts) {
    if (counts.purchased > 0) return 'repeat';
    if (counts.favourites > 0 || counts.viewed >= 3) return 'engaged';
    return 'new';
  }

  function shopperSignalProfile() {
    const entries = buildSignalEntries();
    const counts = {
      purchased: readPurchasedProducts().length,
      favourites: readList(FAV_KEY).length,
      viewed: readList(VIEWED_KEY).length,
    };
    const vendorScores = new Map();
    const themeScores = new Map();
    const signalText = entries
      .map((entry) => productSignalText(entry.product))
      .filter(Boolean)
      .join(' ');

    entries.forEach((entry) => {
      const vendor = String(entry.product.vendor || '').trim();
      if (vendor) {
        vendorScores.set(vendor, (vendorScores.get(vendor) || 0) + entry.weight);
      }

      matchThemeClusters(productSignalText(entry.product)).forEach((cluster) => {
        themeScores.set(cluster.key, {
          key: cluster.key,
          label: cluster.label,
          score: (themeScores.get(cluster.key)?.score || 0) + (entry.weight * cluster.matches.length),
          matches: Array.from(new Set([...(themeScores.get(cluster.key)?.matches || []), ...cluster.matches])),
        });
      });
    });

    const topThemes = Array.from(themeScores.values())
      .sort((left, right) => right.score - left.score)
      .slice(0, 3);
    const topVendors = Array.from(vendorScores.entries())
      .map(([name, score]) => ({ name, score }))
      .sort((left, right) => right.score - left.score)
      .slice(0, 3);

    return {
      counts,
      maturity: shopperMaturity(counts),
      topThemes,
      topTheme: topThemes[0] || null,
      topVendors,
      topVendor: topVendors[0] || null,
      signalText,
    };
  }

  function extractProductTokens(product) {
    return Array.from(new Set([
      product.title,
      product.vendor,
      product.type,
      Array.isArray(product.tags) ? product.tags.join(' ') : '',
    ].join(' ')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .split(/[^a-z0-9]+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 4 && !STOP_WORDS.has(token))));
  }

  function scoreProductAgainstProfile(product, profile) {
    const productText = productSignalText(product);
    const productThemes = matchThemeClusters(productText);
    const themeScore = productThemes.reduce((score, cluster) => {
      const profileTheme = profile.topThemes.find((item) => item.key === cluster.key);
      return score + (profileTheme ? profileTheme.score : 0);
    }, 0);
    const keywordScore = extractProductTokens(product).reduce((score, token) => {
      return profile.signalText.includes(token) ? score + 1 : score;
    }, 0);

    return themeScore + keywordScore;
  }

  function toLiveProduct(product, data) {
    const variants = Array.isArray(data?.variants) ? data.variants : [];
    const exactVariant = variants.find((item) => item && item.id === product.variantId);
    const variant = (exactVariant && exactVariant.available ? exactVariant : null)
      || variants.find((item) => item && item.available)
      || exactVariant
      || variants[0];

    if (!variant) {
      return product;
    }

    return {
      ...product,
      title: data.title || product.title,
      url: product.url || `/products/${product.handle}`,
      vendor: data.vendor || product.vendor,
      type: data.type || product.type,
      tags: Array.isArray(data.tags) ? data.tags : product.tags,
      image: data.featured_image || product.image,
      price: Number(variant.price || product.price || 0),
      compareAtPrice: Number(variant.compare_at_price || product.compareAtPrice || 0),
      variantId: Number(variant.id || product.variantId || 0),
      available: Boolean(variant.available),
      createdAt: product.createdAt || 0,
    };
  }

  function fetchLiveProduct(product) {
    if (!product || !product.handle) {
      return Promise.resolve(product);
    }

    if (liveProductCache.has(product.handle)) {
      return liveProductCache.get(product.handle);
    }

    const request = window.fetch(`/products/${encodeURIComponent(product.handle)}.js`)
      .then((response) => {
        if (!response.ok) throw new Error('Product request failed');
        return response.json();
      })
      .then((data) => toLiveProduct(product, data))
      .catch(() => product);

    liveProductCache.set(product.handle, request);
    return request;
  }

  function fetchLiveProducts(products) {
    return Promise.all(products.map((product) => fetchLiveProduct(product)));
  }

  function filterPriceDrops(products) {
    return fetchLiveProducts(products).then((liveProducts) => {
      return liveProducts
        .map((liveProduct, index) => {
          const previous = products[index];
          const previousPrice = Number(previous?.price || 0);
          const livePrice = Number(liveProduct?.price || 0);

          if (!previousPrice || !livePrice || livePrice >= previousPrice) {
            return null;
          }

          return {
            ...liveProduct,
            compareAtPrice: Math.max(liveProduct.compareAtPrice || 0, previousPrice),
            priceDropAmount: previousPrice - livePrice,
          };
        })
        .filter(Boolean)
        .sort((left, right) => (right.priceDropAmount || 0) - (left.priceDropAmount || 0));
    });
  }

  function filterBackInStock(products) {
    const unavailableFavourites = products.filter((product) => product && product.available === false);

    return fetchLiveProducts(unavailableFavourites).then((liveProducts) => {
      return liveProducts.filter((product, index) => {
        return Boolean(product?.available) && unavailableFavourites[index]?.available === false;
      });
    });
  }

  function moduleWhyText(moduleEl, key, profile, products) {
    const intro = moduleEl.querySelector('.pro-for-you-module__intro');
    if (!intro) return;
    if (!intro.dataset.defaultIntro) {
      intro.dataset.defaultIntro = intro.textContent.trim();
    }

    const topTheme = profile.topTheme?.label || '';
    const topVendors = formatNaturalList(profile.topVendors.slice(0, 2).map((vendor) => vendor.name));
    let text = intro.textContent.trim();

    if (key === 'buy-again' && products.length > 0) {
      text = topVendors
        ? `Why this is here: these come from your order history, with ${topVendors} showing up most often.`
        : 'Why this is here: these come from your order history and are the easiest products to reorder now.';
    } else if (key === 'recently-viewed' && products.length > 0) {
      text = topTheme
        ? `Why this is here: you’ve been browsing ${topTheme} recently, so these are ready to revisit.`
        : 'Why this is here: these are the products you looked at most recently, so it is easy to pick up where you left off.';
    } else if (key === 'favourites' && products.length > 0) {
      text = topTheme
        ? `Why this is here: you’ve saved products across ${topTheme}, so they stay together here for faster return visits.`
        : 'Why this is here: these are the products you have saved for faster access next time.';
    } else if (key === 'back-in-stock' && products.length > 0) {
      text = topTheme
        ? `Why this is here: saved ${topTheme} products that were unavailable before can now be ordered again.`
        : 'Why this is here: these saved products were unavailable before and are now ready to order again.';
    } else if (key === 'price-drops' && products.length > 0) {
      text = topTheme
        ? `Why this is here: these are lower than when you last viewed or saved them, led by ${topTheme}.`
        : 'Why this is here: these are lower than when you last viewed or saved them.';
    } else if (key === 'new-from-brands' && products.length > 0) {
      if (moduleEl.dataset.forYouCandidateSource === 'logged-out-brand-slot') {
        text = 'Why this is here: these are current seasonal offers and stronger-value picks worth a closer look while you shop.';
      } else {
        text = topVendors
          ? `Why this is here: you keep coming back to ${topVendors}, so newer products from those brands appear first.`
          : 'Why this is here: newer arrivals from the brands you already trust show here when we have a strong fit.';
      }
    } else if (key === 'complete-your-routine' && products.length > 0) {
      text = topTheme
        ? `Why this is here: your strongest shopping signal is ${topTheme}, so these help round out that routine.`
        : 'Why this is here: these products help round out what you already buy, view, and save when the fit is strong.';
    }

    intro.textContent = text;
  }

  async function resolveModuleProducts(moduleEl, key, maxItems, profile) {
    const shopperHandles = currentShopperHandles();
    const fallbackProducts = parseFallbackProducts(moduleEl);

    if (key === 'recently-viewed') {
      return {
        list: readList(VIEWED_KEY),
        products: readList(VIEWED_KEY).slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'buy-again') {
      const purchased = readPurchasedProducts()
        .slice()
        .sort((left, right) => Number(right.orderedAt || 0) - Number(left.orderedAt || 0));
      return {
        list: purchased,
        products: purchased.slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'favourites') {
      return {
        list: readList(FAV_KEY),
        products: readList(FAV_KEY).slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'back-in-stock') {
      const favourites = readList(FAV_KEY);
      const list = await filterBackInStock(favourites);
      return {
        list,
        products: list.slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'price-drops') {
      const watched = dedupeByHandle([...readList(VIEWED_KEY), ...readList(FAV_KEY)]);
      const list = await filterPriceDrops(watched);
      return {
        list,
        products: list.slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'new-from-brands') {
      const sourceKey = moduleEl.dataset.forYouCandidateSource || 'brand-matches';
      if (sourceKey === 'logged-out-brand-slot') {
        const candidates = readCandidateProducts(sourceKey)
          .filter((product) => product && product.available);
        return {
          list: candidates,
          products: candidates.slice(0, maxItems),
          fallbackProducts,
        };
      }

      const vendorWeights = new Map(profile.topVendors.map((vendor) => [vendor.name, vendor.score]));
      const candidates = readCandidateProducts(sourceKey)
        .filter((product) => product && product.available && !shopperHandles.has(product.handle))
        .map((product) => ({
          ...product,
          vendorScore: vendorWeights.get(product.vendor) || 0,
        }))
        .filter((product) => product.vendorScore > 0)
        .sort((left, right) => {
          if (right.vendorScore !== left.vendorScore) return right.vendorScore - left.vendorScore;
          return Number(right.createdAt || 0) - Number(left.createdAt || 0);
        });

      return {
        list: candidates,
        products: candidates.slice(0, maxItems),
        fallbackProducts,
      };
    }

    if (key === 'complete-your-routine') {
      const sourceKey = moduleEl.dataset.forYouCandidateSource || 'routine-matches';
      const candidates = readCandidateProducts(sourceKey)
        .filter((product) => product && product.available && !shopperHandles.has(product.handle))
        .map((product) => ({
          ...product,
          matchScore: scoreProductAgainstProfile(product, profile),
        }))
        .sort((left, right) => {
          if (right.matchScore !== left.matchScore) return right.matchScore - left.matchScore;
          return Number(right.createdAt || 0) - Number(left.createdAt || 0);
        });

      const matched = candidates.filter((product) => product.matchScore > 0);

      return {
        list: matched,
        products: matched.slice(0, maxItems),
        fallbackProducts,
      };
    }

    return {
      list: [],
      products: fallbackProducts.slice(0, maxItems),
      fallbackProducts,
    };
  }

  async function renderModule(moduleEl, key, profile) {
    const target = moduleEl.querySelector(`[data-for-you-target="${key}"]`);
    const empty = moduleEl.querySelector(`[data-for-you-empty="${key}"]`);
    const historyLink = moduleEl.querySelector(`[data-for-you-history-link="${key}"]`);
    const intro = moduleEl.querySelector('.pro-for-you-module__intro');
    if (!target || !empty) return;
    if (intro && !intro.dataset.defaultIntro) {
      intro.dataset.defaultIntro = intro.textContent.trim();
    }

    const maxItems = Number(moduleEl.dataset.forYouMaxItems || 6);
    const hideWhenEmpty = moduleEl.dataset.forYouHideWhenEmpty === 'true';
    const { list, products } = await resolveModuleProducts(moduleEl, key, maxItems, profile);

    if (products.length === 0) {
      target.innerHTML = '';
      if (intro?.dataset.defaultIntro) {
        intro.textContent = intro.dataset.defaultIntro;
      }
      empty.hidden = false;
      moduleEl.hidden = hideWhenEmpty;
      moduleEl.dataset.forYouSystemHidden = hideWhenEmpty ? 'true' : 'false';
      moduleEl.classList.remove('is-populated');
      moduleEl.classList.add('is-empty');
      updateHistoryLink(historyLink, key, 0, maxItems);
      return;
    }

    empty.hidden = true;
    moduleEl.hidden = false;
    moduleEl.dataset.forYouSystemHidden = 'false';
    moduleEl.classList.add('is-populated');
    moduleEl.classList.remove('is-empty');
    target.innerHTML = products.map((product) => cardMarkup(product)).join('');
    moduleWhyText(moduleEl, key, profile, products);
    updateHistoryLink(historyLink, key, list.length, maxItems);
  }

  function updateHistoryLink(linkEl, key, count, pageSize) {
    if (!linkEl) return;

    const hasMore = count > pageSize;
    const route = HISTORY_ROUTES[key];
    const footerEl = linkEl.closest('[data-for-you-history-footer]');
    linkEl.classList.toggle('is-disabled', !hasMore);
    linkEl.setAttribute('aria-disabled', hasMore ? 'false' : 'true');
    linkEl.tabIndex = hasMore ? 0 : -1;

    if (footerEl) {
      footerEl.hidden = !hasMore;
    }

    if (hasMore && route) {
      linkEl.setAttribute('href', route);
    } else {
      linkEl.removeAttribute('href');
    }
  }

  function dashboardDefaultOrder(modulesEl, profile = shopperSignalProfile()) {
    const baseOrder = uniqueIds((modulesEl?.dataset?.forYouDefaultOrder || '').split(',').map((item) => item.trim()));
    const scope = modulesEl?.dataset?.forYouDashboardScope || 'guest';
    const isCustomer = scope.startsWith('customer-');
    let preferred = [];

    if (isCustomer) {
      if (profile.maturity === 'repeat') {
        preferred = ['buy-again', 'back-in-stock', 'price-drops', 'recently-viewed', 'favourites', 'complete-your-routine', 'new-from-brands'];
      } else if (profile.maturity === 'engaged') {
        preferred = ['recently-viewed', 'favourites', 'price-drops', 'back-in-stock', 'buy-again', 'complete-your-routine', 'new-from-brands'];
      } else {
        preferred = ['recently-viewed', 'favourites', 'complete-your-routine', 'new-from-brands', 'price-drops', 'back-in-stock', 'buy-again'];
      }
    } else if (profile.maturity === 'engaged') {
      preferred = ['recently-viewed', 'favourites', 'price-drops', 'back-in-stock', 'complete-your-routine', 'new-from-brands'];
    } else {
      preferred = ['recently-viewed', 'new-from-brands', 'favourites', 'complete-your-routine', 'price-drops', 'back-in-stock'];
    }

    return uniqueIds([...preferred.filter((item) => baseOrder.includes(item)), ...baseOrder]);
  }

  function dashboardDefaultCollapsed(modulesEl) {
    return uniqueIds((modulesEl?.dataset?.forYouDefaultCollapsed || '').split(',').map((item) => item.trim()));
  }

  function normalizeDashboardOrder(defaultOrder, defaultCollapsed, prefs) {
    const flexible = uniqueIds(defaultOrder);
    const order = uniqueIds([...(prefs.order || []), ...flexible]).filter((item) => flexible.includes(item));
    const collapsedSource = prefs.hasStoredPrefs ? prefs.collapsed : defaultCollapsed;

    return {
      order,
      collapsed: uniqueIds(collapsedSource).filter((item) => order.includes(item)),
    };
  }

  function syncDashboardModuleControls(modulesEl, state) {
    const desktopDrag = window.matchMedia('(min-width: 990px)').matches;
    const { order, collapsed } = state;
    modulesEl.querySelectorAll('[data-for-you-dashboard-item]').forEach((moduleEl) => {
      const moduleId = moduleEl.dataset.forYouDashboardItem;
      const index = order.indexOf(moduleId);
      const isCollapsed = collapsed.includes(moduleId);
      const toggleButton = moduleEl.querySelector(`[data-for-you-dashboard-toggle="${moduleId}"]`);
      const dragButton = moduleEl.querySelector(`[data-for-you-dashboard-drag="${moduleId}"]`);
      const systemHidden = moduleEl.dataset.forYouSystemHidden === 'true';

      moduleEl.classList.toggle('is-collapsed', isCollapsed && !systemHidden);

      if (toggleButton) {
        const toggleLabel = isCollapsed ? 'Expand module' : 'Collapse module';
        toggleButton.setAttribute('aria-pressed', isCollapsed ? 'true' : 'false');
        toggleButton.setAttribute('aria-label', toggleLabel);
        toggleButton.dataset.tooltip = toggleLabel;
        toggleButton.removeAttribute('title');
      }

      if (dragButton) {
        dragButton.hidden = !desktopDrag;
        dragButton.draggable = desktopDrag;
        dragButton.setAttribute('aria-disabled', desktopDrag ? 'false' : 'true');
        const dragLabel = desktopDrag ? 'Drag to reorder' : 'Drag reorder available on desktop';
        dragButton.dataset.tooltip = dragLabel;
        dragButton.removeAttribute('title');
      }
    });
  }

  function applyDashboardPrefsForModules(modulesEl, profile = shopperSignalProfile()) {
    if (!modulesEl) return;
    const scope = modulesEl.dataset.forYouDashboardScope || 'guest';
    const defaultOrder = dashboardDefaultOrder(modulesEl, profile);
    const defaultCollapsed = dashboardDefaultCollapsed(modulesEl);
    const prefs = normalizeDashboardOrder(defaultOrder, defaultCollapsed, readDashboardPrefs(scope));

    let orderValue = 10;
    prefs.order.forEach((moduleId) => {
      const moduleEl = modulesEl.querySelector(`#${moduleId}`);
      if (!moduleEl) return;
      moduleEl.style.order = String(orderValue++);
      const systemHidden = moduleEl.dataset.forYouSystemHidden === 'true';
      moduleEl.hidden = systemHidden;
    });

    syncDashboardModuleControls(modulesEl, prefs);
  }

  function applyDashboardPrefs(profile = shopperSignalProfile()) {
    document.querySelectorAll('[data-for-you-dashboard-scope]').forEach((modulesEl) => {
      applyDashboardPrefsForModules(modulesEl, profile);
    });
  }

  function reorderItems(items, moduleId, direction) {
    const next = items.slice();
    const index = next.indexOf(moduleId);
    if (index < 0) return next;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= next.length) return next;

    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    return next;
  }

  function moveItemBefore(items, sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) return items.slice();

    const next = items.filter((item) => item !== sourceId);
    const targetIndex = next.indexOf(targetId);
    if (targetIndex < 0) {
      next.push(sourceId);
      return next;
    }

    next.splice(targetIndex, 0, sourceId);
    return next;
  }

  function renderHistoryPage(sectionEl) {
    const key = sectionEl.dataset.forYouHistoryPage;
    const grid = sectionEl.querySelector('[data-for-you-history-grid]');
    const empty = sectionEl.querySelector('[data-for-you-history-empty]');
    const indicator = sectionEl.querySelector('[data-for-you-history-indicator]');
    const prev = sectionEl.querySelector('[data-for-you-history-prev]');
    const next = sectionEl.querySelector('[data-for-you-history-next]');
    if (!grid || !empty || !indicator || !prev || !next) return;

    const pageSize = Number(sectionEl.dataset.forYouPageSize || 12);
    let list = [];

    if (key === 'recently-viewed') {
      list = readList(VIEWED_KEY);
    } else if (key === 'buy-again') {
      list = readPurchasedProducts();
    } else {
      list = readList(FAV_KEY);
    }
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
    let currentPage = Number(sectionEl.dataset.forYouHistoryCurrentPage || 0);

    if (currentPage >= totalPages) currentPage = 0;
    if (currentPage < 0) currentPage = 0;
    sectionEl.dataset.forYouHistoryCurrentPage = String(currentPage);

    if (list.length === 0) {
      grid.innerHTML = '';
      empty.hidden = false;
      indicator.textContent = '';
      prev.disabled = true;
      next.disabled = true;
      prev.setAttribute('aria-disabled', 'true');
      next.setAttribute('aria-disabled', 'true');
      return;
    }

    const start = currentPage * pageSize;
    const pageItems = list.slice(start, start + pageSize);
    empty.hidden = true;
    grid.innerHTML = pageItems.map((product) => cardMarkup(product)).join('');
    indicator.textContent = `Page ${currentPage + 1} of ${totalPages}`;

    prev.disabled = currentPage === 0;
    next.disabled = currentPage >= totalPages - 1;
    prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
    next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
  }

  async function renderModules() {
    const profile = shopperSignalProfile();
    const modules = Array.from(document.querySelectorAll('[data-for-you-module]'));
    await Promise.all(modules.map((moduleEl) => renderModule(moduleEl, moduleEl.dataset.forYouModule, profile)));
    applyDashboardPrefs(profile);
  }

  function renderHistoryPages() {
    document.querySelectorAll('[data-for-you-history-page]').forEach((sectionEl) => {
      renderHistoryPage(sectionEl);
    });
  }

  function scoreGuideCard(card, profile) {
    const keywords = (card.dataset.matchKeywords || '')
      .split(/[\n,]/)
      .map(normalizeToken)
      .filter(Boolean);

    if (keywords.length === 0) {
      return 0;
    }

    const guideThemes = matchThemeClusters(keywords.join(' '));
    const themeScore = guideThemes.reduce((score, cluster) => {
      const profileTheme = profile.topThemes.find((item) => item.key === cluster.key);
      return score + (profileTheme ? profileTheme.score : 0);
    }, 0);
    const keywordScore = keywords.reduce((score, keyword) => {
      return profile.signalText.includes(keyword) ? score + 1 : score;
    }, 0);

    return themeScore + keywordScore;
  }

  function renderGuideSections(profile = shopperSignalProfile()) {
    document.querySelectorAll('[data-for-you-guides]').forEach((sectionEl) => {
      const cards = Array.from(sectionEl.querySelectorAll('[data-for-you-guide-card]'));
      const empty = sectionEl.querySelector('[data-for-you-guides-empty]');
      const state = sectionEl.querySelector('[data-for-you-guides-state]');
      const limit = Number(sectionEl.dataset.forYouGuidesLimit || 3);
      const hasSignals = profile.signalText.length > 0;

      const ranked = cards
        .map((card, index) => ({
          card,
          index,
          score: hasSignals ? scoreGuideCard(card, profile) : 0,
          fallback: Number(card.dataset.defaultRank || index + 1),
        }))
        .sort((left, right) => {
          if (right.score !== left.score) return right.score - left.score;
          return left.fallback - right.fallback;
        });

      let visibleCount = 0;
      ranked.forEach((item) => {
        const shouldShow = visibleCount < limit && (item.score > 0 || !hasSignals);
        item.card.hidden = !shouldShow;
        if (shouldShow) visibleCount += 1;
      });

      if (visibleCount === 0) {
        ranked.slice(0, limit).forEach((item) => {
          item.card.hidden = false;
        });
        visibleCount = Math.min(limit, ranked.length);
      }

      if (empty) {
        empty.hidden = visibleCount > 0;
      }

      if (state) {
        state.textContent = profile.topTheme
          ? `Why this is here: you’ve shown the strongest interest in ${profile.topTheme.label}, so these guides come first.`
          : 'Useful reads for shoppers who want clarity before they buy.';
      }
    });
  }

  function getCarouselStep(viewport) {
    const slide = viewport.querySelector('.pro-for-you-carousel__slide');
    if (!slide) {
      return viewport.clientWidth * 0.85;
    }

    const slideWidth = slide.getBoundingClientRect().width;
    const track = viewport.querySelector('.pro-for-you-carousel__track');
    const gap = track ? parseFloat(window.getComputedStyle(track).gap || '0') : 0;

    return slideWidth + (Number.isFinite(gap) ? gap : 0);
  }

  function updateCarouselControls(carousel) {
    const viewport = carousel.querySelector('[data-for-you-carousel-viewport]');
    const prev = carousel.querySelector('[data-for-you-carousel-prev]');
    const next = carousel.querySelector('[data-for-you-carousel-next]');
    if (!viewport || !prev || !next) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const atStart = viewport.scrollLeft <= 2;
    const atEnd = viewport.scrollLeft >= maxScroll - 2;

    prev.disabled = atStart || maxScroll <= 0;
    next.disabled = atEnd || maxScroll <= 0;
    prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
    next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
  }

  function bindCarousels() {
    document.querySelectorAll('[data-for-you-carousel]').forEach((carousel) => {
      if (carousel.dataset.forYouCarouselBound === 'true') return;
      carousel.dataset.forYouCarouselBound = 'true';

      const viewport = carousel.querySelector('[data-for-you-carousel-viewport]');
      const prev = carousel.querySelector('[data-for-you-carousel-prev]');
      const next = carousel.querySelector('[data-for-you-carousel-next]');
      if (!viewport || !prev || !next) return;

      const scroll = (direction) => {
        viewport.scrollBy({
          left: direction * getCarouselStep(viewport),
          behavior: 'smooth',
        });
      };

      prev.addEventListener('click', (event) => {
        event.preventDefault();
        scroll(-1);
      });

      next.addEventListener('click', (event) => {
        event.preventDefault();
        scroll(1);
      });

      const sync = () => updateCarouselControls(carousel);
      viewport.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  function syncFavouriteButtons() {
    document.querySelectorAll('[data-for-you-favorite-toggle]').forEach((button) => {
      const card = button.closest('[data-for-you-product]');
      const product = getCardData(card);
      if (!product) return;
      const favourite = isFavourite(product.handle);
      button.classList.toggle('is-favourited', favourite);
      button.setAttribute('aria-pressed', favourite ? 'true' : 'false');
      button.innerHTML = favourite ? '♥' : '♡';
    });
  }

  function bindFavouriteButtons() {
    document.querySelectorAll('[data-for-you-favorite-toggle]').forEach((button) => {
      if (button.dataset.forYouBound === 'true') return;
      button.dataset.forYouBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const card = button.closest('[data-for-you-product]');
        const product = getCardData(card);
        toggleFavourite(button, product);
      });
    });
  }

  function bindCardClicks() {
    document.querySelectorAll('[data-for-you-product]').forEach((card) => {
      if (card.dataset.forYouTracked === 'true') return;
      card.dataset.forYouTracked = 'true';
      const product = getCardData(card);
      if (!product) return;

      const link = card.querySelector('a[href]');
      if (!link) return;

      link.addEventListener('click', () => {
        updateRecentlyViewed(product);
      });
    });
  }

  function bindHistoryLinks() {
    document.querySelectorAll('[data-for-you-history-link]').forEach((link) => {
      if (link.dataset.forYouHistoryLinkBound === 'true') return;
      link.dataset.forYouHistoryLinkBound = 'true';

      link.addEventListener('click', (event) => {
        if (link.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        }
      });
    });
  }

  function bindHistoryPagers() {
    document.querySelectorAll('[data-for-you-history-page]').forEach((sectionEl) => {
      if (sectionEl.dataset.forYouHistoryPagerBound === 'true') return;
      sectionEl.dataset.forYouHistoryPagerBound = 'true';

      const prev = sectionEl.querySelector('[data-for-you-history-prev]');
      const next = sectionEl.querySelector('[data-for-you-history-next]');
      if (!prev || !next) return;

      prev.addEventListener('click', () => {
        const currentPage = Number(sectionEl.dataset.forYouHistoryCurrentPage || 0);
        sectionEl.dataset.forYouHistoryCurrentPage = String(Math.max(0, currentPage - 1));
        renderHistoryPage(sectionEl);
        bindFavouriteButtons();
        bindCardClicks();
        syncFavouriteButtons();
      });

      next.addEventListener('click', () => {
        const currentPage = Number(sectionEl.dataset.forYouHistoryCurrentPage || 0);
        sectionEl.dataset.forYouHistoryCurrentPage = String(currentPage + 1);
        renderHistoryPage(sectionEl);
        bindFavouriteButtons();
        bindCardClicks();
        syncFavouriteButtons();
      });
    });
  }

  function bindDashboardControls() {
    document.querySelectorAll('[data-for-you-dashboard-scope]').forEach((modulesEl) => {
      if (modulesEl.dataset.forYouDashboardBound === 'true') return;
      modulesEl.dataset.forYouDashboardBound = 'true';

      const scope = modulesEl.dataset.forYouDashboardScope || 'guest';
      const defaultCollapsed = dashboardDefaultCollapsed(modulesEl);
      let draggingModuleId = '';

      modulesEl.querySelectorAll('[data-for-you-dashboard-toggle]').forEach((toggleButton) => {
        if (toggleButton.dataset.forYouDashboardToggleBound === 'true') return;
        toggleButton.dataset.forYouDashboardToggleBound = 'true';

        toggleButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const moduleId = toggleButton.dataset.forYouDashboardToggle;
          if (!moduleId) return;

          const prefs = normalizeDashboardOrder(
            dashboardDefaultOrder(modulesEl, shopperSignalProfile()),
            defaultCollapsed,
            readDashboardPrefs(scope)
          );
          prefs.collapsed = prefs.collapsed.includes(moduleId)
            ? prefs.collapsed.filter((item) => item !== moduleId)
            : [...prefs.collapsed, moduleId];

          writeDashboardPrefs(scope, prefs);
          applyDashboardPrefsForModules(modulesEl);
        });
      });

      modulesEl.querySelectorAll('[data-for-you-dashboard-drag]').forEach((dragButton) => {
        if (dragButton.dataset.forYouDashboardDragBound === 'true') return;
        dragButton.dataset.forYouDashboardDragBound = 'true';

        dragButton.addEventListener('dragstart', (event) => {
          if (!window.matchMedia('(min-width: 990px)').matches) {
            event.preventDefault();
            return;
          }

          const moduleEl = dragButton.closest('[data-for-you-dashboard-item]');
          draggingModuleId = moduleEl?.dataset.forYouDashboardItem || '';
          if (!draggingModuleId || !moduleEl) {
            event.preventDefault();
            return;
          }

          moduleEl.classList.add('is-dragging');
          if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', draggingModuleId);
          }
        });

        dragButton.addEventListener('dragend', () => {
          draggingModuleId = '';
          modulesEl.querySelectorAll('.is-dragging, .is-drop-target').forEach((el) => {
            el.classList.remove('is-dragging', 'is-drop-target');
          });
        });
      });

      modulesEl.querySelectorAll('[data-for-you-dashboard-item]').forEach((moduleEl) => {
        if (moduleEl.dataset.forYouDashboardDropBound !== 'true') {
          moduleEl.dataset.forYouDashboardDropBound = 'true';

          moduleEl.addEventListener('dragover', (event) => {
            if (!draggingModuleId || moduleEl.dataset.forYouDashboardItem === draggingModuleId) return;
            event.preventDefault();
            modulesEl.querySelectorAll('.is-drop-target').forEach((el) => el.classList.remove('is-drop-target'));
            moduleEl.classList.add('is-drop-target');
            if (event.dataTransfer) {
              event.dataTransfer.dropEffect = 'move';
            }
          });

          moduleEl.addEventListener('dragleave', () => {
            moduleEl.classList.remove('is-drop-target');
          });

          moduleEl.addEventListener('drop', (event) => {
            event.preventDefault();
            const sourceId = event.dataTransfer?.getData('text/plain') || draggingModuleId;
            const targetId = moduleEl.dataset.forYouDashboardItem || '';
            moduleEl.classList.remove('is-drop-target');
            if (!sourceId || !targetId || sourceId === targetId) return;

            const prefs = normalizeDashboardOrder(
              dashboardDefaultOrder(modulesEl, shopperSignalProfile()),
              defaultCollapsed,
              readDashboardPrefs(scope)
            );
            prefs.order = moveItemBefore(prefs.order, sourceId, targetId);
            writeDashboardPrefs(scope, prefs);
            applyDashboardPrefsForModules(modulesEl);
          });
        }
      });

      applyDashboardPrefsForModules(modulesEl, shopperSignalProfile());
    });
  }

  function setShortcutState(navEl, visitedIndexes, currentIndex) {
    const tabs = Array.from(navEl.querySelectorAll('[data-for-you-shortcut-index]'));
    const progress = navEl.parentElement?.querySelector('[data-for-you-progress]');
    if (!progress || tabs.length === 0) return;

    Array.from(progress.querySelectorAll('.pro-for-you__progress-step')).forEach((step, index) => {
      step.classList.toggle('pro-for-you__progress-step--active', visitedIndexes.has(index));
    });

    tabs.forEach((tab, index) => {
      tab.classList.toggle('pro-for-you__tab--active', visitedIndexes.has(index));
    });
  }

  function bindShortcutTabs() {
    document.querySelectorAll('[data-for-you-shortcuts]').forEach((navEl) => {
      if (navEl.dataset.forYouShortcutsBound === 'true') return;
      navEl.dataset.forYouShortcutsBound = 'true';

      const tabs = Array.from(navEl.querySelectorAll('[data-for-you-shortcut-index]'));
      const targets = [];
      const visitedIndexes = new Set();
      let currentIndex = -1;

      const update = () => setShortcutState(navEl, visitedIndexes, currentIndex);
      update();

      const markOpenedModules = () => {
        let changed = false;

        targets.forEach(({ target, index }) => {
          if (!target.matches('[data-for-you-dashboard-item]')) return;
          if (target.hidden || target.classList.contains('is-collapsed')) return;

          if (!visitedIndexes.has(index)) {
            visitedIndexes.add(index);
            changed = true;
          }
        });

        if (changed) update();
      };

      tabs.forEach((tab) => {
        const hash = tab.getAttribute('href') || '';
        if (!hash.startsWith('#')) return;
        const target = document.querySelector(hash);
        if (!target) return;

        const index = Number(tab.dataset.forYouShortcutIndex);
        if (!Number.isFinite(index)) return;

        target.dataset.forYouShortcutTargetIndex = String(index);
        targets.push({ tab, target, index });
      });

      markOpenedModules();

      if ('MutationObserver' in window) {
        targets.forEach(({ target }) => {
          if (!target.matches('[data-for-you-dashboard-item]')) return;

          const observer = new window.MutationObserver(() => {
            markOpenedModules();
          });

          observer.observe(target, {
            attributes: true,
            attributeFilter: ['class', 'hidden'],
          });
        });
      }

      if (!('IntersectionObserver' in window)) return;

      const observer = new window.IntersectionObserver((entries) => {
        let changed = false;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (entry.target.matches('[data-for-you-dashboard-item]') && entry.target.classList.contains('is-collapsed')) {
            return;
          }

          const index = Number(entry.target.dataset.forYouShortcutTargetIndex);
          if (!Number.isFinite(index)) return;

          if (!visitedIndexes.has(index)) {
            visitedIndexes.add(index);
            changed = true;
          }

          if (currentIndex !== index) {
            currentIndex = index;
            changed = true;
          }
        });

        if (changed) update();
      }, {
        threshold: 0.45,
        rootMargin: '-12% 0px -35% 0px',
      });

      targets.forEach(({ target }) => {
        observer.observe(target);
      });
    });
  }

  function trackCurrentProduct() {
    const current = getCardData(document.querySelector('[data-for-you-current-product]'));
    if (!current) return;
    updateRecentlyViewed(current);
  }

  async function init() {
    trackCurrentProduct();
    await renderModules();
    renderHistoryPages();
    renderGuideSections();
    bindFavouriteButtons();
    bindCardClicks();
    bindShortcutTabs();
    bindHistoryLinks();
    bindHistoryPagers();
    bindDashboardControls();
    bindCarousels();
    syncFavouriteButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
    });
  } else {
    init();
  }
})();
